import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../../data/content";
import { Menu, X, ChevronDown } from "lucide-react";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  return (
    <>
    <div data-testid="pt-badge-bar" className="bg-[#0B132B] py-1.5 text-center text-xs font-medium tracking-wide text-white">
      <span className="text-gold">●</span> <a href="tel:+12154454257" className="hover:text-gold">(215) 445-4257</a>
    </div>
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "glass border-slate-200" : "border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8">
        <Link to="/" data-testid="nav-domus-logo" aria-label="Vertexhub Ventures home">
          <Logo />
        </Link>

        <div className="hidden items-center gap-0.5 2xl:flex">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <div key={link.label} className="group relative">
                <button className="flex items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-gold">
                  {link.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <div className="invisible absolute left-0 top-full w-64 translate-y-2 rounded-xl border border-slate-200 bg-[#F5F3EC] p-2 opacity-0 shadow-2xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  {link.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className="block rounded-lg px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-[#EDEAE1] hover:text-gold"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className="whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-gold"
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden shrink-0 items-center gap-2 2xl:flex">
          <a href="tel:+12154454257" data-testid="nav-phone" className="hidden whitespace-nowrap px-2 text-sm font-semibold text-[#0B132B] hover:text-gold 2xl:inline">(215) 445-4257</a>
          <Link
            to="/client-login"
            data-testid="nav-client-login-button"
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-gold hover:text-gold"
          >
            Client Login
          </Link>
          <Link
            to="/property-inspections/book"
            data-testid="nav-book-inspection-button"
            className="whitespace-nowrap rounded-full bg-gold px-5 py-2 text-sm font-semibold text-[#0B132B] transition-all hover:bg-[#E5C158] hover:shadow-[0_0_20px_rgba(212,175,55,0.35)]"
          >
            Book an Inspection
          </Link>
        </div>

        <button
          className="2xl:hidden text-[#0B132B]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="border-t border-slate-200 bg-[#F5F3EC] px-4 py-4 2xl:hidden">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <div key={link.label} className="border-b border-slate-200 py-1">
                <button
                  className="flex w-full items-center justify-between py-2 text-left text-sm font-semibold text-[#0B132B]"
                  onClick={() => setOpenGroup(openGroup === link.label ? null : link.label)}
                >
                  {link.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${openGroup === link.label ? "rotate-180" : ""}`} />
                </button>
                {openGroup === link.label && (
                  <div className="pb-2 pl-3">
                    {link.children.map((c) => (
                      <Link key={c.to} to={c.to} className="block py-2 text-sm text-slate-600 hover:text-gold">
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link key={link.to} to={link.to} className="block border-b border-slate-200 py-3 text-sm font-semibold text-[#0B132B] hover:text-gold">
                {link.label}
              </Link>
            )
          )}
          <div className="mt-4 flex flex-col gap-2">
            <Link to="/client-login" className="rounded-full border border-slate-300 px-4 py-2 text-center text-sm text-slate-700">Client Login</Link>
            <Link to="/property-inspections/book" className="rounded-full bg-gold px-4 py-2 text-center text-sm font-semibold text-[#0B132B]">Book an Inspection</Link>
          </div>
        </div>
      )}
    </header>
    </>
  );
};
